/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1788912000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Sep 09 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "rokt",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Rokt",
          "depth": 0,
          "id": "connect-to-rokt"
        }, {
          "value": "Select an API version",
          "depth": 1,
          "id": "select-an-api-version"
        }, {
          "value": "Current API credentials",
          "depth": 1,
          "id": "current-api-credentials"
        }, {
          "value": "Legacy API credentials",
          "depth": 1,
          "id": "legacy-api-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Event",
          "depth": 1,
          "id": "event"
        }, {
          "value": "Segment",
          "depth": 1,
          "id": "segment"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Rokt is an ecommerce marketing platform for personalized offers and ads. Hightouch sends conversion events to Rokt for campaign optimization and attribution, and keeps Rokt audiences up to date for targeting and suppression."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#event",
                children: _jsx(_components.strong, {
                  children: "Event"
                })
              })
            }), _jsx(_components.td, {
              children: "Send conversion events to Rokt for campaign optimization and attribution"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.rokt.com/developers/integration-guides/getting-started/advertisers/ads-events-api-integration/",
                children: "Send conversion events ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#segment",
                children: _jsx(_components.strong, {
                  children: "Segment"
                })
              })
            }), _jsx(_components.td, {
              children: "Create Rokt audiences and keep their membership up to date for targeting or suppression"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.rokt.com/developers/integration-guides/getting-started/advertisers/ads-audience-api-integration/",
                children: "Update audience membership ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-rokt",
        children: "Connect to Rokt"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Rokt"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "select-an-api-version",
        children: "Select an API version"
      }), "\n", _jsxs(_components.p, {
        children: ["Rokt has deprecated its legacy Event and Audience APIs. Select ", _jsx(_components.strong, {
          children: "Current"
        }), " as the ", _jsx(_components.strong, {
          children: "API Version"
        }), " for new connections; the legacy APIs no longer receive updates, and Rokt recommends migrating existing connections."]
      }), "\n", _jsxs(_components.p, {
        children: ["Rokt destinations created before the ", _jsx(_components.strong, {
          children: "API Version"
        }), " setting existed remain on the legacy APIs. To migrate one, set its ", _jsx(_components.strong, {
          children: "API Version"
        }), " to ", _jsx(_components.strong, {
          children: "Current"
        }), " and enter the ", _jsx(_components.a, {
          href: "#current-api-credentials",
          children: "current API credentials"
        }), ". Hightouch keeps your legacy credentials saved, so you can switch back if needed."]
      }), "\n", _jsx(_components.h3, {
        id: "current-api-credentials",
        children: "Current API credentials"
      }), "\n", _jsx(_components.p, {
        children: "The current Event and Audience APIs authenticate with a single server-to-server key pair:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Server to Server Key"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Server to Server Secret"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Request the key pair from your Rokt account manager. Rokt's current APIs run on mParticle infrastructure, so if you manage your Rokt integration through mParticle, you can instead copy the values from the Hightouch feed under ", _jsx(_components.strong, {
          children: "Setup > Inputs"
        }), " in the ", _jsx(_components.a, {
          href: "https://app.mparticle.com/data-platform/setup/inputs/",
          children: "mParticle dashboard"
        }), " — see the ", _jsx(_components.a, {
          href: "/destinations/mparticle",
          children: "mParticle docs"
        }), " for how to create the feed."]
      }), "\n", _jsxs(_components.p, {
        children: ["One key pair covers both the Event and Segment integrations. The key's region prefix (for example ", _jsx(_components.code, {
          children: "us1-"
        }), " or ", _jsx(_components.code, {
          children: "us2-"
        }), ") determines which Rokt data center Hightouch sends events to; a key with no prefix routes to US1."]
      }), "\n", _jsx(_components.h3, {
        id: "legacy-api-credentials",
        children: "Legacy API credentials"
      }), "\n", _jsx(_components.p, {
        children: "The legacy APIs use separate credentials per integration. Enter only the credentials for the sync types you use."
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Event"
        }), " syncs:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "App ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "App Secret"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account ID"
          }), ": You can find this in the URL of your Rokt account. Go to your profile settings and the URL will look like this: ", _jsx(_components.code, {
            children: "https://my.rokt.com/accounts/<accountId>/profile-settings"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find the App ID and App Secret at the bottom of your Rokt profile settings. If you haven't created an app yet, create an app and generate the two values. For more information, refer to ", _jsx(_components.a, {
          href: "https://docs.rokt.com/developers/api-reference/event-api#generating-app-id-and-app-secret",
          children: "Rokt's docs"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Segment"
        }), " syncs, you also need to enter your ", _jsx(_components.strong, {
          children: "API Key"
        }), ". You can retrieve one by following the instructions in ", _jsx(_components.a, {
          href: "https://docs.rokt.com/developers/api-reference/custom-audience-import/#authorization",
          children: "Rokt's docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Rokt destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Rokt destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "event",
        children: "Event"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "your source"
        }), " as new events and sends them to Rokt when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsx(_components.p, {
        children: "If you select a column, it should be in UTC and RFC 3339. On the legacy API, Rokt rejects events with a timestamp older than 18 months or more than 5 minutes in the future; events outside this range aren't processed."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync model columns to Rokt's event properties, including custom fields. You must include mappings for the ", _jsx(_components.strong, {
          children: "Client Event ID"
        }), " and ", _jsx(_components.strong, {
          children: "Event Type"
        }), " fields. Rokt deduplicates events on the ", _jsx(_components.strong, {
          children: "Client Event ID"
        }), ", so map a value that's unique per event, such as an order or confirmation number."]
      }), "\n", _jsxs(_components.p, {
        children: ["On the current API, each event must also include at least one identifier: ", _jsx(_components.strong, {
          children: "Email"
        }), ", ", _jsx(_components.strong, {
          children: "Email (SHA-256 Hash)"
        }), ", or ", _jsx(_components.strong, {
          children: "Passback Conversion Tracking ID"
        }), ". Rokt rejects events without one."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you capture the Rokt click ID from your ad interactions, map it to ", _jsx(_components.strong, {
          children: "Passback Conversion Tracking ID"
        }), ". Rokt uses it to match conversions back to clicks, and Hightouch sends it in both fields Rokt matches on."]
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch automatically hashes plaintext values mapped to the ", _jsx(_components.strong, {
          children: "(SHA-256 Hash)"
        }), " variants of the following fields before sending them to Rokt:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Mobile Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "IP Address"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can disable this behavior in the sync configuration. If disabled, the data from the model should be appropriately normalized and hashed according to ", _jsx(_components.a, {
          href: "https://docs.rokt.com/developers/integration-guides/data-and-security/pii-data-hashing/",
          children: "Rokt's hashing requirements"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "segment",
        children: "Segment"
      }), "\n", _jsx(_components.p, {
        children: "Use segment syncs to add users to a Rokt audience and remove them as they leave your model. You can use these audiences to target or exclude specific customers in your campaigns, or to determine which customers experience Rokt on your site."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "The identifiers you can map depend on the destination's API version."
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "current"
        }), " API, select one or more of these model columns to identify users:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email (SHA256 Hash)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Customer ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "IDFA (iOS advertising ID)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "GAID (Android advertising ID)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Phone (SHA256 Hash)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Postal code"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Rokt matches the identifiers against its network and resolves them to a single user, so mapping more identifiers increases the share of your audience Rokt can reach. Rokt recommends including an email identifier."
      }), "\n", _jsx(_components.p, {
        children: "At least one mapping must be a hashed email, customer ID, IDFA, or GAID. Phone, name, and address values enrich a match but can't anchor one, so Hightouch rejects rows without one of the anchor identifiers. Rokt can only match a customer ID it has already seen, for example from your Event syncs."
      }), "\n", _jsxs(_components.p, {
        children: ["The current API doesn't support MD5-hashed emails. Use ", _jsx(_components.strong, {
          children: "Email (SHA256 Hash)"
        }), " instead."]
      }), "\n", _jsxs(_components.p, {
        children: ["On the ", _jsx(_components.strong, {
          children: "legacy"
        }), " API, map exactly one email identifier: ", _jsx(_components.strong, {
          children: "Email"
        }), ", ", _jsx(_components.strong, {
          children: "Email (MD5 Hash)"
        }), ", or ", _jsx(_components.strong, {
          children: "Email (SHA256 Hash)"
        }), "."]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Rokt audiences"
      }), "\n", _jsx(_components.h4, {
        id: "phone-number-formatting",
        children: "Phone number formatting"
      }), "\n", _jsxs(_components.p, {
        children: ["Rokt hashes phone numbers as the national number, without the country code. When you map ", _jsx(_components.strong, {
          children: "Phone (SHA256 Hash)"
        }), ", Hightouch removes the country code before hashing. Select a ", _jsx(_components.strong, {
          children: "Default country"
        }), " for numbers that arrive without one."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-audience-or-create-a-new-one",
        children: "Select an existing audience or create a new one"
      }), "\n", _jsx(_components.p, {
        children: "You can create a new audience or use an existing one. When creating a new audience, you can optionally enter a name; otherwise, Hightouch defaults to the name of the associated model. To use an existing audience, select the desired audience from the dropdown."
      }), "\n", _jsx(_components.p, {
        children: "Rokt creates the audience when the first record syncs to it. Audience names are limited to 100 characters."
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing-1",
        children: "Handling PII and hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically hashes unhashed emails — and, on the current API, phone numbers — before sending them to Rokt. You can disable this behavior in the sync configuration."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Rokt, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "To confirm events are arriving, check conversion reporting in Rokt or coordinate with your Rokt account manager. For audiences, confirm the audience appears in your Rokt account after the first sync completes."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
